import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { fetchCategoryParams } from '../services/parameters';

interface ParameterContextType {
  parameters: Record<string, unknown>;
  featureToggles: Record<string, boolean>;
  loading: boolean;
  error: string | null;
  getParam: (key: string, defaultValue?: unknown) => unknown;
  isModuleEnabled: (moduleName: string) => boolean;
  refresh: () => void;
}

const ParameterContext = createContext<ParameterContextType | null>(null);

export function ParameterConfigProvider({ children }: { children: ReactNode }) {
  const [parameters, setParameters] = useState<Record<string, unknown>>({});
  const [featureToggles, setFeatureToggles] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function loadConfig() {
    setLoading(true);
    setError(null);
    fetchCategoryParams('ui')
      .then((uiData: { parameters?: Record<string, unknown> }) => {
        const params = uiData?.parameters ?? {};

        const toggleKeys = [
          'module.screening_enabled',
          'module.monitoring_enabled',
          'module.reports_enabled',
          'module.investigations_enabled',
          'module.fatca_enabled',
          'module.crawler_enabled',
          'module.unusual_ops_enabled',
          'module.rbac_enabled',
          'module.customer_classification_enabled',
          'module.pep_management_enabled',
          'module.audit_log_enabled',
        ];

        const toggles: Record<string, boolean> = {};
        for (const key of toggleKeys) {
          toggles[key] = params[key] !== undefined ? Boolean(params[key]) : true;
        }

        setParameters(params);
        setFeatureToggles(toggles);
      })
      .catch((err: Error) => {
        console.warn('ParameterConfigProvider: Failed to fetch UI config', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadConfig();
  }, []);

  function getParam(key: string, defaultValue: unknown = null): unknown {
    return parameters[key] !== undefined ? parameters[key] : defaultValue;
  }

  function isModuleEnabled(moduleName: string): boolean {
    const key = `module.${moduleName}_enabled`;
    return featureToggles[key] !== undefined ? featureToggles[key] : true;
  }

  function refresh() {
    loadConfig();
  }

  const value: ParameterContextType = {
    parameters,
    featureToggles,
    loading,
    error,
    getParam,
    isModuleEnabled,
    refresh,
  };

  return <ParameterContext.Provider value={value}>{children}</ParameterContext.Provider>;
}

export function useParameterConfig(): ParameterContextType {
  const ctx = useContext(ParameterContext);
  if (!ctx) {
    return {
      parameters: {},
      featureToggles: {},
      loading: false,
      error: null,
      getParam: (_key: string, def?: unknown) => def ?? null,
      isModuleEnabled: () => true,
      refresh: () => {},
    };
  }
  return ctx;
}

export default ParameterContext;
